import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import './index.scss'
import { DaterangeValue } from '../../components/Input/Daterange'
import { useTranslation } from 'react-i18next'
import UserContext from '../../contexts/UserContext/context'
import FleetSelect, { FleetSelectValue } from '../../components/FleetSelect'
import Card from './Card'
import Button from '../../components/Button'
import Icon from '../../components/Icon'
import { ICON } from '../../components/Icon/data'
import { COLOR } from '../../data/variables'
import Infobox from '../../components/Infobox'
import Panel from './Panel'
import { useQuery } from 'react-query'
import { getDashboard, updateDashboard } from '../../services/calls/dashboard'
import { CARD_MAP } from './Card/data'
import { isValue } from '../../services/utils/misc'
import ButtonDropdown from '../../components/ButtonDropdown'
import Input from '../../components/Input'
import { DateTime } from 'luxon'

const Dashboard = () => {
  const { t } = useTranslation()
  const { locale } = DateTime.local()
  const cardRefs = useRef<Record<number, HTMLDivElement | null>>({})

  const { user } = useContext(UserContext)

  const [customMode, setCustomMode] = useState<boolean>(false)
  const [dates, setDates] = useState<DaterangeValue>({
    from: DateTime.now().toISODate(),
    to: DateTime.now().toISODate()
  })
  const [fleet, setFleet] = useState<FleetSelectValue | null>(user?.available_fleets.find((val) => val.fleetid === user?.main_fleet) ? {
    value: user?.main_fleet, name: user.available_fleets.find((val) => val.fleetid === user?.main_fleet)?.name } : null
  )
  const [activePos, setActivePos] = useState<string[]>([])
  const [dragType, setDragType] = useState<string | null>(null)

  const dashboardParams = useMemo(() => ({
    fleetid: fleet?.value ?? undefined,
    chosen_date: dates.from,
    interval: dates.to === dates.from
      ? 1
      : Math.ceil(DateTime.fromISO(dates.to).diff(DateTime.fromISO(dates.from)).as('days'))
  }), [fleet, dates])

  const { data, isLoading } = useQuery(['getDashboard', dashboardParams], () => getDashboard(dashboardParams))

  useEffect(() => {
    if (data) {
      const cardPos = data?.selected_dashboards?.length ? data.selected_dashboards : Object.keys(CARD_MAP)
      setActivePos(cardPos)
    }
  }, [data])

  useEffect(() => {
    if (!dragType && activePos.length) {
      const newPos = [...activePos].filter((key) => isValue(key))
      setActivePos(newPos)
    }
  }, [dragType])

  const onMouseMove = useCallback((e: Event) => {
    if (isValue(dragType) && customMode) {
      const { x, y } = e
      // Calculate the closest card
      let closest = 'holder'
      let minDist = Infinity
      activePos.forEach((key) => {
        const point = cardRefs?.current[key]?.getPosition()
        if (point) {
          const dx = point.x + (point.width / 2) - x
          const dy = point.y + (point.height / 2) - y
          const dist = Math.sqrt(dx * dx + dy * dy) // Euclidean distance
          if (dist < minDist) {
            minDist = dist
            closest = key
          }
        }
      })

      // Which side is the mouse hovering?
      const point = cardRefs?.current[closest]?.getPosition() ?? {}
      const side = x > (point.x + (point.width / 2)) ? 'right' : 'left' 
      const newPos = [...activePos].reduce((arr, val) => {
        if (val === null) return arr
        if (val === closest && closest !== 'holder' && side === 'left') arr.push(null)
        if (isValue(val) && val !== 'holder') arr.push(val)
        if (val === closest && closest !== 'holder' && side === 'right') arr.push(null)
        return arr
      }, [])
      setActivePos(newPos)
    }
  }, [dragType, cardRefs?.current, activePos, customMode])

  const dateFilterHeader = useMemo(() => {
    const yesterday = DateTime.now().minus({ days: 1 }).toISODate() // The endpoint returns the day before
    const fromDate = DateTime.fromISO(dates.from)
    const toDate = DateTime.fromISO(dates.to)
    if (
      fromDate.minus({ days: 1 }).toISODate() === yesterday &&
      toDate.minus({ days: 1 }).toISODate() === yesterday
    ) {
      return `${t('Yesterday')}, ${DateTime.fromISO(yesterday).toLocaleString(DateTime.DATE_FULL)}`
    } else if (fromDate.diff(DateTime.fromISO(dates.to)).as('days') === 0) {
      return fromDate.toLocaleString(DateTime.DATETIME_HUGE)
    }

    return `${fromDate.toFormat('d. LLLL yyyy')} - ${toDate.toFormat('d. LLLL yyyy')}`
  }, [dates, locale])

  const customButtonLabel = useMemo(() => {
    const label = customMode ? t('Customise view is ON') : t('Customise view')
    return (
      <>
        {label}
        <Icon icon={ICON.MOVE} color={customMode ? COLOR.WHITE : COLOR.TEXT_GRAY} />
      </>
    )
  }, [t, customMode])

  const cards = useMemo(() => {
    const active = (activePos?.length ? activePos : Object.keys(CARD_MAP))
    const inactive = Object.keys(CARD_MAP).filter((key) => !active.includes(key))

    return { active, inactive }
  }, [activePos, dragType])

  const onCardDrop = useCallback((type: string) => {
    if (type) {
      const newCardPos = activePos.reduce((arr, key) => {
        if (key === type) return arr
        if (key === null) arr.push(type)
        else arr.push(key)
        return arr
      }, [])
      setActivePos(newCardPos)
      updateDashboard({ dashboards: newCardPos })
      setDragType(null)
    }
  }, [activePos])

  const eventDrop = useCallback(() => {
    if (dragType) onCardDrop(dragType)
  }, [dragType, onCardDrop])

  useEffect(() => {
    if (customMode) {
      document.addEventListener('mousemove', onMouseMove)
      document.addEventListener('mouseup', eventDrop)
    }

    return () => {
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', eventDrop)
    }
  }, [customMode, onMouseMove])

  const removeCard = useCallback((type: string) => {
    const newPos = [...activePos]
    const idx = newPos.indexOf(type)
    if (idx > -1) {
      newPos.splice(idx, 1)
      setActivePos(newPos)
      updateDashboard({ dashboards: newPos })
    }
  }, [activePos])

  const dateDropdown = useMemo(() => {
    return (
      <div className="Dashboard__filters-filter__dates">
        <Infobox
          type="inline"
          status="info"
          label={t('Note')}
          message={t("The data shown will be referred to the previous day to the one that's been selected.")}
        />
        <Input
          type="daterange"
          size='s'
          hideLabel={false}
          value={dates}
          onInput={(val) => {
            const valFrom = DateTime.fromISO(val.from).toISODate()
            const valTo = DateTime.fromISO(val.to).toISODate()
            setDates({ from: valFrom, to: valTo })
          }}
        />
      </div>
    )
  }, [dates, t])

  return (
    <div className="Dashboard">
      <div className="Dashboard__header">
        <h1>{t('Your Dashboard')}</h1>
        <Button
          size='s'
          type={customMode ? 'secondary' : 'tertiary'}
          label={customButtonLabel}
          onClick={() => setCustomMode(!customMode)}
        />
      </div>
      <div className="Dashboard__filters">
        <div className="Dashboard__filters-filter">
          <span className="Dashboard__filters-filter__label">{dateFilterHeader}</span>
          <ButtonDropdown
            label={(
              <>
                {t('Custom range')}
                <Icon icon={ICON.CALENDAR} />
              </>
            )}
            content={dateDropdown}
          />
        </div>
        <div className="Dashboard__filters-filter">
          <span className="Dashboard__filters-filter__label">{fleet?.name}</span>
          <FleetSelect
            onInput={val => setFleet(val)}
            value={fleet?.value}
            size='s'
          />
        </div>
      </div>
      {customMode && (
        <Infobox
          type='inline'
          status='info'
          closable={false}
          label={t('Customize view mode is ON')}
          message={t('Drag and drop panels to change their position and press x to make panels inactive. Inactive panels can be dragged back to make them active.')}
        />
      )}
      <Panel type={customMode ? 'active' : 'none'}>
        <div className="Dashboard__cards">
          {cards.active.map((_key) => {
            const { full_range, interval_range } = data ?? {}
            const key = _key ?? dragType
            const { path } = CARD_MAP[key]
            const isHolder = !_key
            return (
              <Card
                ref={(ref) => cardRefs.current[isHolder ? 'holder' : _key] = ref}
                type={key ?? dragType}
                value={interval_range?.[path ?? key] ?? data?.[path ?? key]}
                secondaryValue={full_range?.[path ?? key] ?? data?.[path ?? key]}
                isDraggable={customMode}
                isHolder={isHolder}
                data={data}
                onDrag={(val) => {
                  if (val) setDragType(val)
                }}
                isRemovable={customMode}
                onRemove={() => removeCard(key)}
                isLoading={isLoading}
              />
            )

            return null
          })}
        </div>
      </Panel>
      {customMode && (
        <Panel type={customMode ? 'inactive' : 'none'} >
          <div className="Dashboard__cards">
            {cards.inactive.map((key) => {
              if (data) {
                const { full_range, interval_range } = data
                const { path } = CARD_MAP[key]
                return (
                  <Card
                    type={key}
                    value={interval_range[key] ?? data[path?? key]}
                    secondaryValue={full_range[key] ?? data[path ?? key]}
                    isDraggable={customMode}
                    isLoading={false}
                    data={data}
                    onDrop={(val) => onCardDrop(val)}
                    onDrag={(val) => {
                      if (val) setDragType(val)
                    }}
                  />
                )
              }

              return null
            })}
          </div>
        </Panel>
      )}
    </div>
  )
}

export default Dashboard
